import { Layouts } from 'app-components';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { ErrorModal } from '@components/errorModal/ErrorModal';
import { configs } from '@constants/configs/configs';
import NavBarContainer from '@containers/common/NavBarContainer';

import { AdvanceSearch } from './AdvanceSearch/AdvanceSearch';
import { DefaultSearch } from './DefaultSearch/DefaultSearch';
import { ResultsSection } from './ResultsSection/ResultsSection';
import { TicketHeader } from './TicketHeader/TicketHeader';
import { PERSIST_REPORT_FILTERS_STORAGE_KEY, SearchType } from './constants/ticketlist.constants';
import { StyledContentCard, StyledTabButton, StyledTabWrapper } from './constants/ticketlist.styled';
import { useTicketListContainer } from './useTicketListContainer';

export const TicketListContainer = () => {
    const {
        isAdvanceSearch,
        setSelectedSearchType,
        fetchDefaultTickets,
        tickets,
        searchCriteria,
        ticketsCount,
        isInitialLoad,
        onClaimNewTicket,
        onAddNewTicket,
        workgroupOptions,
        onErrorModalClose,
        isErrorModalOpen,
        errorMessage,
        modalTitle,
        enableTicketSupport,
        viewTicketPermission,
        fetchFilteredTickets,
        isReportPageRedirect,
        resetTrigger,
        setResetTrigger,
        isWatchTicketAllowed,
        isManageWatcherAllowed,
        confidentialTicketList,
    } = useTicketListContainer();
    const {
        routes,
    } = configs;

    const selectDefaultSearch = () => {
        if (isAdvanceSearch) {
            setSelectedSearchType(SearchType.DEFAULT);
            fetchFilteredTickets();
        }
    };

    const selectAdvanceSearch = () => {
        if (!isAdvanceSearch) {
            setSelectedSearchType(SearchType.ADVANCE);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const isDueDateReport = searchParams.get('dueDateReport') === 'true';
        if (!isDueDateReport) {
            return undefined;
        }

        const persistReportFilters = () => {
            const nextUrl = window.location.pathname;
            if (nextUrl === routes.REPORT_DUEDATE_CHARTS) {
                sessionStorage.setItem(PERSIST_REPORT_FILTERS_STORAGE_KEY, 'true');
            }
        };

        window.addEventListener('popstate', persistReportFilters);

        return () => {
            window.removeEventListener('popstate', persistReportFilters);
            persistReportFilters();
        };
    }, [routes.REPORT_DUEDATE_CHARTS]);
    return (
        <Layouts id="manage-tickets">
            <NavBarContainer navBarClass="ticket-mgmt-top-nav grid-col-12" />
            <TicketHeader
                fetchDefaultTickets={fetchDefaultTickets}
                ticketsCount={ticketsCount}
                onClaimNewTicket={onClaimNewTicket}
                onAddNewTicket={onAddNewTicket}
                workgroupOptions={workgroupOptions}
                enableTicketSupport={enableTicketSupport}
                viewTicketPermission={viewTicketPermission}
                isReportPageRedirect={isReportPageRedirect}
            />
            <ConditionalRendering isTrue={isReportPageRedirect}>
                <StyledTabWrapper role="tablist">
                    <StyledTabButton
                        role="tab"
                        aria-selected={!isAdvanceSearch}
                        tabIndex={!isAdvanceSearch ? -1 : 0}
                        data-testid="search-my-ticket"
                        onClick={selectDefaultSearch}
                    >
                        <FormattedMessage id="ticketList.tab.searchMyTicket" />
                    </StyledTabButton>
                    <ConditionalRendering isTrue={viewTicketPermission}>
                        <StyledTabButton
                            role="tab"
                            aria-selected={isAdvanceSearch}
                            tabIndex={isAdvanceSearch ? -1 : 0}
                            data-testid="advance-search"
                            onClick={selectAdvanceSearch}
                        >
                            <FormattedMessage id="ticketList.tab.advanceSearch" />
                        </StyledTabButton>
                    </ConditionalRendering>
                </StyledTabWrapper>
            </ConditionalRendering>
            <StyledContentCard id="main-content" data-testid="main-content">
                <ConditionalRendering isTrue={isReportPageRedirect}>
                    <ConditionalRendering isTrue={!isAdvanceSearch}>
                        <DefaultSearch
                            workgroupOptions={workgroupOptions}
                            isInitialLoad={isInitialLoad}
                            isWatchTicketAllowed={isWatchTicketAllowed}
                        />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={isAdvanceSearch}>
                        <AdvanceSearch
                            resetTrigger={resetTrigger}
                            setResetTrigger={setResetTrigger}
                            isWatchTicketAllowed={isWatchTicketAllowed}
                        />
                    </ConditionalRendering>
                </ConditionalRendering>
                <ResultsSection
                    tickets={tickets}
                    searchCriteria={searchCriteria}
                    ticketsCount={ticketsCount}
                    isAdvanceSearch={isAdvanceSearch}
                    viewTicketPermission={viewTicketPermission}
                    resetTrigger={resetTrigger}
                    isWatchTicketAllowed={isWatchTicketAllowed}
                    isManageWatcherAllowed={isManageWatcherAllowed}
                    isReportPageRedirect={isReportPageRedirect}
                    confidentialTicketList={confidentialTicketList}
                />
            </StyledContentCard>
            <ConditionalRendering isTrue={isErrorModalOpen}>
                <ErrorModal onClose={onErrorModalClose} errorText={errorMessage} titleId={modalTitle} />
            </ConditionalRendering>
        </Layouts>
    );
};
